import { useCallback, useEffect, useMemo, useState } from 'react';
import { COMPETITION_SEED, completeLeagueMatchday, completeLeagueSeason, createChampionsDraw, createDoubleRoundRobinSchedule, createLeagueGroups, createStandings, findClubFixture, findClubGroup, nextLeagueSeed, playerRating, selectRivalLineup, type LeagueGroup, type RivalTeam } from './competition';
import { advanceClubProgress, clubAchievements, createClubProgress, normaliseClubProgress } from './clubProgression';
import { formationById, formations, playerById, players, starterPlayerIds, techniqueById, teams } from './data';
import { applyDevelopmentAwards, createDevelopmentLog, emptyDevelopment, normaliseDevelopment, resolveDevelopedPlayer, rollMatchDevelopment, SPIRIT_COST, type MatchDevelopmentOutcome } from './development';
import { validateAssignedRole } from './engine/ai/TacticalRoleSystem';
import { SeededRng } from './simulation/rng';
import { leagueDate, nextCalendarDay, nextClubFixture, resolveChampionsTie, simulateAffinityGamesOnDate, simulateChampionsOnDate, simulateKingsCupOnDate, simulateLeagueOnDate } from './careerCalendar';
import { AFFINITY_GAMES_COMPETITION_ID, AFFINITY_USER_TEAM_ID, KINGS_CUP_COMPETITION_ID, affinityLineup, affinityTokenId, createAffinityGames, createKingsCupDraw, neutralWinner } from './specialCompetitions';
import type { AffinityGames, AffinityGamesResult, ChampionsResult, CompetitionHonour, CompetitionResult, ElementType, Formation, KingsCupResult, MatchEvent, MatchResult, Player, PlayerDevelopment, PlayerStatKey, SaveGame, SeasonReview, TacticalRole, Tactics } from './types';

const SAVE_KEY = 'inazuma-manager-web-save-v1';
const BENCH_LIMIT = 7;
export const MASTER_CHOICE_TOKEN_ID = 'token_master_choice';
export const TECHNIQUE_CHOICE_TOKEN_ID = 'token_technique_choice';
const MASTER_TICKET_GRANT_VERSION = 7;
const GAME_SAVE_VERSION = 19;
const QUALIFICATION_TEST_GRANT_VERSION = 15;
const ELEMENT_TOKEN_IDS = ['token_fire', 'token_wind', 'token_forest', 'token_mountain'] as const;
const SPIRIT_RETURN_WEIGHT = .22;
export type MatchRewardOutcome = 'win' | 'draw' | 'loss';
export type RecruitResult = { player: Player; techniques: string[] };

type CachedSaveEnvelope = { save: Partial<SaveGame>; cachedAt: number };
type SaveBootstrap = { save: SaveGame; recoveredFromLocal: boolean };

const defaultTactics: Tactics = {
  mentality: 0.56,
  pressing: 0.58,
  tempo: 0.54,
  width: 0.5,
  defensiveLine: 0.52,
  risk: 0.46,
  techniqueAggression: 0.55,
};

export const initialSave: SaveGame = {
  version: GAME_SAVE_VERSION,
  season: 1,
  gameMode: 'classic',
  clubName: 'Aurora Eleven',
  selectedTeamId: 'team_aurora',
  clubCrestId: 'aurora',
  ownedPlayerIds: [...starterPlayerIds],
  lineup: [...teams[0].lineup],
  benchPlayerIds: [],
  captainPlayerId: teams[0].lineup[0],
  formationId: teams[0].formation_id,
  customFormations: [],
  tacticalRoleOverrides: {},
  tactics: defaultTactics,
  tokens: {
    token_fire: 2,
    token_wind: 2,
    token_forest: 2,
    token_mountain: 2,
    token_youth: 3,
    [MASTER_CHOICE_TOKEN_ID]: 5,
    [TECHNIQUE_CHOICE_TOKEN_ID]: 0,
  },
  recruits: [],
  clubTechniqueIds: {},
  clubProgress: createClubProgress(),
  lastResult: null,
  leagueMatchday: 0,
  leagueResults: [],
  leaguePointAdjustments: {},
  seasonReview: null,
  leagueSeed: COMPETITION_SEED,
  championsDraw: null,
  championsResults: [],
  kingsCupDraw: null,
  kingsCupResult: null,
  affinityGames: null,
  affinityGamesResults: [],
  competitionHonours: [],
  careerDate: '2026-09-01',
  clubDevelopment: {},
  worldDevelopment: {},
  developmentLog: [],
  spiritReleasedPlayerIds: [],
  recruitmentSeed: 1469598103,
  nextSeed: 847293,
};

/** Uses browser entropy rather than network state. The value is persisted in
 * the save, so reloads are reproducible for an in-progress batch but a new
 * career never starts from a public, fixed gacha sequence. */
export function createEntropySeed(): number {
  try {
    const values = new Uint32Array(1);
    globalThis.crypto?.getRandomValues(values);
    if (values[0]) return values[0];
  } catch {
    // Some private browsers intentionally block Web Crypto. The fallback is
    // still local and does not require a network connection.
  }
  const time = Date.now() >>> 0;
  const noise = Math.floor(Math.random() * 0xffffffff) >>> 0;
  return (time ^ noise ^ 0x9e3779b9) >>> 0 || 1;
}

export function createFreshSave(): SaveGame {
  return {
    ...initialSave,
    ownedPlayerIds: [...initialSave.ownedPlayerIds],
    lineup: [...initialSave.lineup],
    benchPlayerIds: [...initialSave.benchPlayerIds],
    tacticalRoleOverrides: {},
    tokens: { ...initialSave.tokens },
    clubTechniqueIds: {},
    clubProgress: createClubProgress(),
    clubDevelopment: {},
    worldDevelopment: {},
    developmentLog: [],
    spiritReleasedPlayerIds: [],
    recruitmentSeed: createEntropySeed(),
    nextSeed: createEntropySeed(),
  };
}

function readCachedSave(storageKey: string): { save: SaveGame; cachedAt: number } | undefined {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<SaveGame> | CachedSaveEnvelope;
    const envelope = parsed && typeof parsed === 'object' && 'save' in parsed
      && typeof (parsed as CachedSaveEnvelope).cachedAt === 'number'
      ? parsed as CachedSaveEnvelope
      : undefined;
    return {
      save: migrateSave(envelope?.save ?? parsed as Partial<SaveGame>),
      // Legacy snapshots had no envelope. They were explicitly written by an
      // older local client, so prefer them once and immediately upgrade them.
      cachedAt: envelope?.cachedAt ?? Number.MAX_SAFE_INTEGER,
    };
  } catch {
    return undefined;
  }
}

function readSave(storageKey = SAVE_KEY): SaveGame {
  return readCachedSave(storageKey)?.save ?? createFreshSave();
}

export function migrateSave(parsed: Partial<SaveGame>): SaveGame {
  // One-off test grant so existing local and cloud slots can exercise the
  // master selector without repeatedly replenishing spent tickets.
  const isBeforeMasterTicketGrant = (Number(parsed.version) || 0) < MASTER_TICKET_GRANT_VERSION;
  const isBeforeQualificationTestGrant = (Number(parsed.version) || 0) < QUALIFICATION_TEST_GRANT_VERSION;
  const requestedOwned = Array.isArray(parsed.ownedPlayerIds) ? parsed.ownedPlayerIds : [];
  const validOwned = requestedOwned.filter((id) => playerById.has(id));
  const containedRemovedFixtures = requestedOwned.some((id) => !playerById.has(id));
  const requestedLineup = Array.isArray(parsed.lineup) ? parsed.lineup.filter((id) => playerById.has(id)) : [];
  const lineup = requestedLineup.length === 11 && new Set(requestedLineup).size === 11
    ? requestedLineup
    : [...initialSave.lineup];
  const ownedPlayerIds = [...new Set([
    ...(containedRemovedFixtures || validOwned.length < 11 ? starterPlayerIds : []),
    ...validOwned,
    ...lineup,
  ])];
  const captainPlayerId = typeof parsed.captainPlayerId === 'string' && lineup.includes(parsed.captainPlayerId)
    ? parsed.captainPlayerId
    : lineup[0];
  const requestedBench = Array.isArray(parsed.benchPlayerIds) ? parsed.benchPlayerIds : undefined;
  const benchPlayerIds = buildBench(ownedPlayerIds, lineup, requestedBench);
  const requestedCustomFormations = Array.isArray(parsed.customFormations) ? parsed.customFormations : [];
  const customFormations = requestedCustomFormations.filter((formation): formation is Formation => (
    typeof formation?.id === 'string'
    && typeof formation.name === 'string'
    && Array.isArray(formation.roles)
    && Array.isArray(formation.positions)
    && formation.roles.length === 11
    && formation.positions.length === 11
    && formation.positions.every((position) => Array.isArray(position) && position.length === 2 && position.every((value) => typeof value === 'number'))
  ));
  const formationExists = (formationId: string) => formationById.has(formationId) || customFormations.some((formation) => formation.id === formationId);
  const recruits = normaliseRecruits(parsed.recruits);
  let migrated: SaveGame = {
    ...initialSave,
    ...parsed,
    version: GAME_SAVE_VERSION,
    gameMode: parsed.gameMode === 'draft' ? 'draft' : 'classic',
    selectedTeamId: typeof parsed.selectedTeamId === 'string' ? parsed.selectedTeamId : initialSave.selectedTeamId,
    clubCrestId: typeof parsed.clubCrestId === 'string' ? parsed.clubCrestId : initialSave.clubCrestId,
    formationId: parsed.formationId && formationExists(parsed.formationId) ? parsed.formationId : initialSave.formationId,
    customFormations,
    ownedPlayerIds,
    lineup,
    benchPlayerIds,
    captainPlayerId,
    tacticalRoleOverrides: normaliseTacticalRoleOverrides(parsed.tacticalRoleOverrides, ownedPlayerIds),
    recruits,
    clubTechniqueIds: normaliseClubTechniqueIds(parsed.clubTechniqueIds, recruits),
    clubProgress: normaliseClubProgress(parsed.clubProgress),
    leagueMatchday: Math.max(0, Number(parsed.leagueMatchday) || 0),
    leagueResults: Array.isArray(parsed.leagueResults) ? parsed.leagueResults : [],
    leaguePointAdjustments: normalisePointAdjustments(parsed.leaguePointAdjustments),
    seasonReview: normaliseSeasonReview(parsed.seasonReview),
    leagueSeed: Number.isFinite(Number(parsed.leagueSeed)) && Number(parsed.leagueSeed) > 0 ? Number(parsed.leagueSeed) : COMPETITION_SEED,
    championsDraw: isChampionsDraw(parsed.championsDraw) ? parsed.championsDraw : null,
    championsResults: Array.isArray(parsed.championsResults) ? parsed.championsResults.filter((result): result is import('./types').ChampionsResult => Boolean(result && typeof result.fixtureId === 'string' && typeof result.winnerId === 'string' && typeof result.round === 'string')) : [],
    kingsCupDraw: isKingsCupDraw(parsed.kingsCupDraw) ? parsed.kingsCupDraw : null,
    kingsCupResult: isKingsCupResult(parsed.kingsCupResult) ? parsed.kingsCupResult : null,
    affinityGames: normaliseAffinityGames(parsed.affinityGames, formationExists),
    affinityGamesResults: Array.isArray(parsed.affinityGamesResults)
      ? parsed.affinityGamesResults.filter((result): result is AffinityGamesResult => Boolean(result && typeof result.fixtureId === 'string' && (result.phase === 'GROUP' || result.phase === 'FINAL')))
      : [],
    competitionHonours: normaliseCompetitionHonours(parsed.competitionHonours),
    careerDate: typeof parsed.careerDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(parsed.careerDate) ? parsed.careerDate : `${2025 + (Number(parsed.season) || 1)}-09-01`,
    clubDevelopment: normaliseDevelopmentMap(parsed.clubDevelopment),
    worldDevelopment: normaliseDevelopmentMap(parsed.worldDevelopment),
    developmentLog: normaliseDevelopmentLog(parsed.developmentLog),
    spiritReleasedPlayerIds: [...new Set((Array.isArray(parsed.spiritReleasedPlayerIds) ? parsed.spiritReleasedPlayerIds : [])
      .filter((playerId): playerId is string => typeof playerId === 'string' && playerById.has(playerId)))],
    recruitmentSeed: Number.isFinite(Number(parsed.recruitmentSeed)) && Number(parsed.recruitmentSeed) > 0
      ? Number(parsed.recruitmentSeed) >>> 0
      : createEntropySeed(),
    nextSeed: Number.isFinite(Number(parsed.nextSeed)) && Number(parsed.nextSeed) > 0
      ? Number(parsed.nextSeed) >>> 0
      : createEntropySeed(),
    tactics: { ...defaultTactics, ...parsed.tactics },
    tokens: {
      ...initialSave.tokens,
      ...parsed.tokens,
      [MASTER_CHOICE_TOKEN_ID]: isBeforeMasterTicketGrant
        ? Math.max(5, Number(parsed.tokens?.[MASTER_CHOICE_TOKEN_ID]) || 0)
        : Math.max(0, Number(parsed.tokens?.[MASTER_CHOICE_TOKEN_ID]) || 0),
    },
  };
  // Temporary, one-off fixture requested for the active test career. It is a
  // table adjustment rather than fabricated matches, so goals and appearances
  // remain honest while Champions qualification uses the visible standings.
  if (isBeforeQualificationTestGrant && migrated.clubName === 'Inazuma Manager Prueba') {
    migrated.leaguePointAdjustments = { ...migrated.leaguePointAdjustments, team_aurora: (migrated.leaguePointAdjustments.team_aurora ?? 0) + 15 };
  }
  // A save created just before the honours ledger existed can still contain a
  // completed special final from the current season. Preserve that verified
  // winner during migration instead of requiring the fixture to be replayed.
  migrated = recordResolvedCompetitionHonours(migrated, migrated.championsResults, migrated.kingsCupResult ? [migrated.kingsCupResult] : [], migrated.affinityGamesResults);
  // Existing saves could already be parked after their final league fixture.
  // Migrate them directly to a valid new draw instead of showing an undefined
  // opponent until another match is somehow played.
  const group = findClubGroup(createLeagueGroups(migrated.leagueSeed, migrated.clubName));
  const schedule = createDoubleRoundRobinSchedule(group.teams.map((team) => team.id));
  return schedule.length && migrated.leagueMatchday >= schedule.length && canRolloverSeason(migrated)
    ? rolloverSeason(migrated)
    : migrated;
}

export function resolveClubPlayer(save: SaveGame, playerId: string): Player | undefined {
  const player = playerById.get(playerId);
  if (!player) return undefined;
  const developed = resolveDevelopedPlayer(player, save.clubDevelopment[playerId]);
  // The database stores every technique a character may learn. Club copies
  // instead carry the techniques rolled at recruitment, which is what the
  // lineup, tactics and match engine must display and execute.
  return { ...developed, techniques: clubTechniqueIdsFor(save, playerId, developed) };
}

export function clubTechniqueIdsFor(save: SaveGame, playerId: string, player = playerById.get(playerId)): string[] {
  const assigned = save.clubTechniqueIds[playerId];
  if (assigned) return [...assigned];
  return [...(player?.techniques ?? [])];
}

export function resolveWorldPlayer(save: SaveGame, playerId: string): Player | undefined {
  const player = playerById.get(playerId);
  return player ? resolveDevelopedPlayer(player, save.worldDevelopment[playerId]) : undefined;
}

export function useGameState(initialState?: SaveGame, storageKey = SAVE_KEY, remoteUpdatedAt?: string) {
  const [bootstrap] = useState<SaveBootstrap>(() => {
    if (!initialState) return { save: readSave(storageKey), recoveredFromLocal: false };
    const remote = migrateSave(initialState);
    const cached = readCachedSave(storageKey);
    const remoteUpdatedAtMs = Date.parse(remoteUpdatedAt ?? '') || 0;
    const useCached = Boolean(cached && (cached.cachedAt >= remoteUpdatedAtMs || cached.cachedAt === Number.MAX_SAFE_INTEGER));
    const localDiffers = useCached && JSON.stringify(cached!.save) !== JSON.stringify(remote);
    return { save: useCached ? cached!.save : remote, recoveredFromLocal: Boolean(localDiffers) };
  });
  const [save, setSave] = useState<SaveGame>(() => bootstrap.save);

  useEffect(() => {
    const snapshot: CachedSaveEnvelope = { save, cachedAt: Date.now() };
    localStorage.setItem(storageKey, JSON.stringify(snapshot));
  }, [save, storageKey]);

  const ownedPlayers = useMemo(
    () => save.ownedPlayerIds.map((id) => resolveClubPlayer(save, id)).filter(Boolean) as Player[],
    [save],
  );

  const setFormation = useCallback((formationId: string) => {
    setSave((current) => {
      const candidates = [...new Set([...current.lineup, ...current.benchPlayerIds, ...current.ownedPlayerIds])];
      const lineup = arrangeLineup(formationId, candidates, current.customFormations);
      return lineup ? {
        ...current,
        formationId,
        lineup,
        benchPlayerIds: buildBench(current.ownedPlayerIds, lineup, current.benchPlayerIds, current.lineup),
        captainPlayerId: lineup.includes(current.captainPlayerId) ? current.captainPlayerId : lineup[0],
      } : current;
    });
  }, []);

  /** The national selection has its own board. Keeping this mutation separate
   * makes it impossible for an Affinity adjustment to overwrite the club XI. */
  const setAffinityFormation = useCallback((formationId: string) => {
    setSave((current) => {
      const event = current.affinityGames;
      const hasFormation = formationById.has(formationId) || current.customFormations.some((formation) => formation.id === formationId);
      if (!event?.squadPlayerIds.length || !hasFormation) return current;
      const lineup = arrangeLineup(formationId, event.squadPlayerIds, current.customFormations);
      if (!lineup) return current;
      return {
        ...current,
        affinityGames: { ...event, formationId, lineupPlayerIds: lineup, benchPlayerIds: event.squadPlayerIds.filter((id) => !lineup.includes(id)) },
      };
    });
  }, []);

  const createCustomFormation = useCallback((name: string, baseFormationId: string) => {
    setSave((current) => {
      const base = formationById.get(baseFormationId) ?? current.customFormations.find((formation) => formation.id === baseFormationId);
      if (!base) return current;
      const formation: Formation = {
        id: `custom_${Date.now()}_${current.customFormations.length + 1}`,
        name: name.trim().slice(0, 32) || `Variante ${base.name}`,
        roles: [...base.roles],
        positions: base.positions.map(([x, y]) => [x, y] as [number, number]),
        modifiers: { ...base.modifiers },
      };
      const candidates = [...new Set([...current.lineup, ...current.benchPlayerIds, ...current.ownedPlayerIds])];
      const lineup = arrangeLineupForFormation(formation, candidates);
      if (!lineup) return current;
      return {
        ...current,
        formationId: formation.id,
        lineup,
        benchPlayerIds: buildBench(current.ownedPlayerIds, lineup, current.benchPlayerIds, current.lineup),
        captainPlayerId: lineup.includes(current.captainPlayerId) ? current.captainPlayerId : lineup[0],
        customFormations: [...current.customFormations, formation].slice(-24),
      };
    });
  }, []);

  const updateCustomFormationLayout = useCallback((formationId: string, positions: Formation['positions']) => {
    if (!formationId.startsWith('custom_') || positions.length !== 11) return;
    const cleanPositions = positions.map(([x, y]) => [
      Math.round(Math.min(97, Math.max(3, x)) * 10) / 10,
      Math.round(Math.min(96, Math.max(4, y)) * 10) / 10,
    ] as [number, number]);
    setSave((current) => {
      const exists = current.customFormations.some((formation) => formation.id === formationId);
      if (!exists) return current;
      return {
        ...current,
        customFormations: current.customFormations.map((formation) => formation.id === formationId
          ? { ...formation, positions: cleanPositions }
          : formation),
      };
    });
  }, []);

  const deleteCustomFormation = useCallback((formationId: string) => {
    if (!formationId.startsWith('custom_')) return;
    setSave((current) => {
      if (!current.customFormations.some((formation) => formation.id === formationId)) return current;
      const customFormations = current.customFormations.filter((formation) => formation.id !== formationId);
      if (current.formationId !== formationId) return { ...current, customFormations };

      const fallbackFormationId = formations[0].id;
      const candidates = [...new Set([...current.lineup, ...current.benchPlayerIds, ...current.ownedPlayerIds])];
      const lineup = arrangeLineup(fallbackFormationId, candidates, customFormations) ?? current.lineup;
      return {
        ...current,
        customFormations,
        formationId: fallbackFormationId,
        lineup,
        benchPlayerIds: buildBench(current.ownedPlayerIds, lineup, current.benchPlayerIds, current.lineup),
        captainPlayerId: lineup.includes(current.captainPlayerId) ? current.captainPlayerId : lineup[0],
      };
    });
  }, []);

  const setTactic = useCallback((key: keyof Tactics, value: number) => {
    setSave((current) => ({ ...current, tactics: { ...current.tactics, [key]: value } }));
  }, []);

  const moveLineupPlayer = useCallback((playerId: string, targetIndex: number) => {
    setSave((current) => {
      if (targetIndex < 0 || targetIndex >= current.lineup.length || !current.ownedPlayerIds.includes(playerId)) {
        return current;
      }
      const lineup = [...current.lineup];
      let benchPlayerIds = [...current.benchPlayerIds];
      const sourceIndex = lineup.indexOf(playerId);
      if (sourceIndex >= 0) {
        [lineup[sourceIndex], lineup[targetIndex]] = [lineup[targetIndex], lineup[sourceIndex]];
      } else {
        if (!benchPlayerIds.includes(playerId)) return current;
        const outgoingPlayerId = lineup[targetIndex];
        lineup[targetIndex] = playerId;
        benchPlayerIds = buildBench(current.ownedPlayerIds, lineup, [outgoingPlayerId, ...benchPlayerIds.filter((id) => id !== playerId)]);
      }
      return {
        ...current,
        lineup,
        benchPlayerIds,
        captainPlayerId: lineup.includes(current.captainPlayerId) ? current.captainPlayerId : lineup[targetIndex],
      };
    });
  }, []);

  const moveAffinityLineupPlayer = useCallback((playerId: string, targetIndex: number) => {
    setSave((current) => {
      const event = current.affinityGames;
      if (!event || targetIndex < 0 || targetIndex >= 11 || !event.squadPlayerIds.includes(playerId)) return current;
      const lineup = [...event.lineupPlayerIds];
      if (lineup.length !== 11) return current;
      const sourceIndex = lineup.indexOf(playerId);
      if (sourceIndex >= 0) [lineup[sourceIndex], lineup[targetIndex]] = [lineup[targetIndex], lineup[sourceIndex]];
      else {
        const outgoing = lineup[targetIndex];
        lineup[targetIndex] = playerId;
        if (outgoing === playerId) return current;
      }
      return { ...current, affinityGames: { ...event, lineupPlayerIds: lineup, benchPlayerIds: event.squadPlayerIds.filter((id) => !lineup.includes(id)) } };
    });
  }, []);

  const swapReservePlayer = useCallback((reservePlayerId: string, targetPlayerId: string) => {
    setSave((current) => {
      const reserveIds = current.ownedPlayerIds.filter((id) => !current.lineup.includes(id) && !current.benchPlayerIds.includes(id));
      if (!reserveIds.includes(reservePlayerId)) return current;
      const lineupIndex = current.lineup.indexOf(targetPlayerId);
      const benchIndex = current.benchPlayerIds.indexOf(targetPlayerId);
      if (lineupIndex < 0 && benchIndex < 0) return current;

      const lineup = [...current.lineup];
      const benchPlayerIds = [...current.benchPlayerIds];
      if (lineupIndex >= 0) lineup[lineupIndex] = reservePlayerId;
      else benchPlayerIds[benchIndex] = reservePlayerId;

      return {
        ...current,
        lineup,
        benchPlayerIds,
        captainPlayerId: lineup.includes(current.captainPlayerId) ? current.captainPlayerId : lineup[lineupIndex >= 0 ? lineupIndex : 0],
      };
    });
  }, []);

  const setCaptain = useCallback((playerId: string) => {
    setSave((current) => current.lineup.includes(playerId) ? { ...current, captainPlayerId: playerId } : current);
  }, []);

  /** A manual role replaces the formation default for this footballer. Passing
   * null returns them to the role naturally inferred from their active slot. */
  const setTacticalRole = useCallback((playerId: string, role: TacticalRole | null) => {
    setSave((current) => {
      const player = playerById.get(playerId);
      if (!player || !current.ownedPlayerIds.includes(playerId)) return current;
      const overrides = { ...current.tacticalRoleOverrides };
      if (role === null) {
        if (!(playerId in overrides)) return current;
        delete overrides[playerId];
      } else {
        if (!validateAssignedRole(player.position, role)) return current;
        overrides[playerId] = role;
      }
      return { ...current, tacticalRoleOverrides: overrides };
    });
  }, []);

  const setAffinityTacticalRole = useCallback((playerId: string, role: TacticalRole | null) => {
    setSave((current) => {
      const event = current.affinityGames;
      const player = playerById.get(playerId);
      if (!event || !player || !event.squadPlayerIds.includes(playerId)) return current;
      const overrides = { ...event.tacticalRoleOverrides };
      if (role === null) delete overrides[playerId];
      else {
        if (!validateAssignedRole(player.position, role)) return current;
        overrides[playerId] = role;
      }
      return { ...current, affinityGames: { ...event, tacticalRoleOverrides: overrides } };
    });
  }, []);

  const recruitBatch = useCallback((tokenId: string, requestedAmount: number, selectedPlayerId?: string): RecruitResult[] => {
    const plan = planRecruitmentBatch(save, tokenId, requestedAmount, selectedPlayerId);
    if (!plan.results.length) return [];
    setSave((current) => applyRecruitmentPlan(current, save, tokenId, plan));
    return plan.results;
  }, [save]);

  const recruit = useCallback((tokenId: string, selectedPlayerId?: string): RecruitResult | null => (
    recruitBatch(tokenId, 1, selectedPlayerId)[0] ?? null
  ), [recruitBatch]);

  const consumeSpirits = useCallback((targetPlayerId: string, stat: PlayerStatKey, donorIds: string[]) => {
    setSave((current) => {
      const donors = [...new Set(donorIds)];
      const reserves = current.ownedPlayerIds.filter((id) => !current.lineup.includes(id) && !current.benchPlayerIds.includes(id));
      if (!current.ownedPlayerIds.includes(targetPlayerId) || donors.length !== SPIRIT_COST || donors.includes(targetPlayerId) || !donors.every((id) => reserves.includes(id))) return current;
      const target = resolveClubPlayer(current, targetPlayerId);
      if (!target || !Number.isFinite(target.stats[stat])) return current;
      const targetDevelopment = normaliseDevelopment(current.clubDevelopment[targetPlayerId]);
      const entry = {
        id: `${current.season}:spirit:${current.nextSeed}:${targetPlayerId}:${stat}`,
        playerId: targetPlayerId,
        stat,
        source: 'spirit' as const,
        season: current.season,
        createdAt: new Date().toISOString(),
      };
      const clubDevelopment = { ...current.clubDevelopment };
      donors.forEach((id) => delete clubDevelopment[id]);
      const clubTechniqueIds = { ...current.clubTechniqueIds };
      donors.forEach((id) => delete clubTechniqueIds[id]);
      clubDevelopment[targetPlayerId] = {
        ...targetDevelopment,
        boosts: { ...targetDevelopment.boosts, [stat]: (targetDevelopment.boosts[stat] ?? 0) + 1 },
        spiritSacrifices: targetDevelopment.spiritSacrifices + SPIRIT_COST,
      };
      const progressUpdate = advanceClubProgress(current.clubProgress, stat === 'kick' ? { kickPlayerIds: [targetPlayerId] } : {});
      return {
        ...current,
        ownedPlayerIds: current.ownedPlayerIds.filter((id) => !donors.includes(id)),
        clubDevelopment,
        clubTechniqueIds,
        // A released player is not erased from the world catalogue. It returns
        // to the appropriate ticket pool with a lower selection weight, which
        // lets the club rediscover it without crowding out new profiles.
        spiritReleasedPlayerIds: [...new Set([...current.spiritReleasedPlayerIds, ...donors])],
        developmentLog: [entry, ...current.developmentLog].slice(0, 120),
        clubProgress: progressUpdate.progress,
        tokens: addTokenRewards(current.tokens, progressUpdate.rewards),
      };
    });
  }, []);

  const redeemTechniqueChoice = useCallback((playerId: string, techniqueId: string) => {
    setSave((current) => {
      if ((current.tokens[TECHNIQUE_CHOICE_TOKEN_ID] ?? 0) <= 0 || !current.ownedPlayerIds.includes(playerId)) return current;
      const player = playerById.get(playerId);
      if (!player || !techniqueById.has(techniqueId)) return current;
      const available = new Set(player.available_techniques ?? player.techniques ?? []);
      const assigned = clubTechniqueIdsFor(current, playerId, player);
      if (!available.has(techniqueId) || assigned.includes(techniqueId)) return current;
      return {
        ...current,
        tokens: { ...current.tokens, [TECHNIQUE_CHOICE_TOKEN_ID]: Math.max(0, (current.tokens[TECHNIQUE_CHOICE_TOKEN_ID] ?? 0) - 1) },
        clubTechniqueIds: { ...current.clubTechniqueIds, [playerId]: [...assigned, techniqueId] },
      };
    });
  }, []);

  /** Locks a temporary, mono-affinity national side without touching the
   * manager's club eleven, substitutes, formation or owned-player list. */
  const confirmAffinitySquad = useCallback((playerIds: string[]) => {
    setSave((current) => {
      const event = current.affinityGames;
      if (!event || event.squadPlayerIds.length) return current;
      const selected = [...new Set(playerIds)].slice(0, 22);
      const valid = selected.length === 22
        // The Affinity Games roster is manager-picked from the entire player
        // catalogue for the assigned element, not from a seeded shortlist.
        && selected.every((id) => playerById.get(id)?.element === event.element)
        && selected.some((id) => playerById.get(id)?.position === 'GK');
      const lineup = valid ? affinityLineup(selected) : null;
      if (!lineup) return current;
      return {
        ...current,
        affinityGames: {
          ...event,
          squadPlayerIds: selected,
          formationId: 'formation_4_4_2',
          lineupPlayerIds: lineup,
          benchPlayerIds: selected.filter((id) => !lineup.includes(id)),
          tacticalRoleOverrides: {},
          presentationComplete: false,
        },
      };
    });
  }, []);

  const completeAffinityPresentation = useCallback(() => {
    setSave((current) => current.affinityGames && current.affinityGames.squadPlayerIds.length && !current.affinityGames.presentationComplete
      ? { ...current, affinityGames: { ...current.affinityGames, presentationComplete: true } }
      : current);
  }, []);

  const registerResult = useCallback((match: MatchResult, competition: 'league' | 'champions' | 'kings_cup' | 'affinity_games' = 'league', playedOn?: string) => {
    setSave((current) => {
      if (competition === 'champions') return applyPlayedCareerDate(registerChampionsResult(current, match), playedOn, current.careerDate);
      if (competition === 'kings_cup') return applyPlayedCareerDate(registerKingsCupResult(current, match), playedOn, current.careerDate);
      if (competition === 'affinity_games') return applyPlayedCareerDate(registerAffinityGamesResult(current, match), playedOn, current.careerDate);
      const groups = createLeagueGroups(current.leagueSeed, current.clubName);
      const group = findClubGroup(groups);
      const schedule = createDoubleRoundRobinSchedule(group.teams.map((team) => team.id));
      if (current.leagueMatchday >= schedule.length) return rolloverSeason(current);
      const clubFixture = findClubFixture(group, current.leagueMatchday);
      const clubIsHome = clubFixture?.fixture.homeId === 'team_aurora';
      const clubGoals = clubIsHome ? match.homeScore : match.awayScore;
      const rivalGoals = clubIsHome ? match.awayScore : match.homeScore;
      const outcome: MatchRewardOutcome = clubGoals > rivalGoals ? 'win' : clubGoals === rivalGoals ? 'draw' : 'loss';
      // Growth has to matter to the rival world too. Background fixtures use
      // the effective ratings, while the user's fixture remains the live result.
      const developedGroup = groupWithCurrentDevelopment(group, current);
      const matchdayResults = clubFixture
        ? completeLeagueMatchday(developedGroup, current.leagueMatchday, [match.homeScore, match.awayScore], match.seed)
        : [];

      const clubSide: 0 | 1 = clubIsHome ? 0 : 1;
      const clubPlayers = current.lineup.map((id) => resolveClubPlayer(current, id)).filter(Boolean) as Player[];
      const clubRoll = rollMatchDevelopment(clubPlayers, match.events, clubSide, outcome, match.seed);
      const clubDevelopment = applyDevelopmentAwards(
        current.clubDevelopment,
        clubRoll.awards,
        (id) => playerById.get(id),
        current.lineup,
        outcome,
        clubRoll.mvpId,
      );

      const opponentSide: 0 | 1 = clubSide === 0 ? 1 : 0;
      const opponentOutcome: MatchDevelopmentOutcome = outcome === 'win' ? 'loss' : outcome === 'loss' ? 'win' : 'draw';
      const opponentIds = (opponentSide === 0 ? match.home.lineup : match.away.lineup);
      const opponentPlayers = opponentIds.map((id) => resolveWorldPlayer(current, id)).filter(Boolean) as Player[];
      const opponentRoll = rollMatchDevelopment(opponentPlayers, match.events, opponentSide, opponentOutcome, match.seed);
      let worldDevelopment = applyDevelopmentAwards(current.worldDevelopment, opponentRoll.awards, (id) => playerById.get(id), opponentIds, opponentOutcome, opponentRoll.mvpId);
      worldDevelopment = progressSimulatedRivals(worldDevelopment, group, matchdayResults, match.seed);

      const developmentLog = [...createDevelopmentLog(clubRoll.awards, current.season), ...current.developmentLog].slice(0, 120);
      const progressUpdate = advanceClubProgress(current.clubProgress, {
        wins: outcome === 'win' ? 1 : 0,
        kickPlayerIds: clubRoll.awards.filter((award) => award.stat === 'kick').map((award) => award.playerId),
      });
      const afterMatch: SaveGame = {
        ...current,
        lastResult: { homeScore: match.homeScore, awayScore: match.awayScore, seed: match.seed },
        // A manually directed league fixture is still a career-day event.
        // Keep its result visible on the actual match date instead of leaving
        // the calendar cursor on the day from which the manager launched it.
        careerDate: current.careerDate > (playedOn ?? leagueDate(current.season, current.leagueMatchday + 1))
          ? current.careerDate
          : (playedOn ?? leagueDate(current.season, current.leagueMatchday + 1)),
        leagueMatchday: current.leagueMatchday + 1,
        leagueResults: [...current.leagueResults, ...matchdayResults],
        nextSeed: match.seed + 104729,
        tokens: addTokenRewards(addTokenRewards(current.tokens, rollMatchTokenRewards(match.seed, outcome)), progressUpdate.rewards),
        clubDevelopment,
        worldDevelopment,
        developmentLog,
        clubProgress: progressUpdate.progress,
      };
      return afterMatch.leagueMatchday >= schedule.length && canRolloverSeason(afterMatch) ? rolloverSeason(afterMatch) : afterMatch;
    });
  }, []);

  /** Advances one career day for the manager calendar. League and Champions
   * fixtures, including the club's, are internally resolved here; opening a
   * match manually remains the alternative when the manager wants to play it. */
  const advanceCareerDay = useCallback(() => {
    setSave((current) => {
      const date = nextCalendarDay(current.careerDate);
      // The ten-day window is a real career interruption: the calendar can
      // reach the announcement, but cannot silently simulate past it and
      // rob the manager of the temporary international selection.
      if (current.affinityGames && !current.affinityGames.squadPlayerIds.length && date > current.affinityGames.announcementDate) return current;
      const simulated = simulateChampionsOnDate(current, date);
      const leagueResults = simulateLeagueOnDate(current, date);
      const kingsCupResults = simulateKingsCupOnDate(current, date);
      const affinityResults = simulateAffinityGamesOnDate(current, date);
      const calendarDevelopment = progressCalendarDevelopment(current, [...leagueResults, ...simulated]);
      const clubLeagueResult = leagueResults.find((result) => result.homeId === 'team_aurora' || result.awayId === 'team_aurora');
      const clubChampionsResult = simulated.find((result) => result.homeId === 'team_aurora' || result.awayId === 'team_aurora');
      const clubKingsCupResult = kingsCupResults.find((result) => result.homeId === 'team_aurora' || result.awayId === 'team_aurora');
      const clubAffinityResult = affinityResults.find((result) => result.homeId === AFFINITY_USER_TEAM_ID || result.awayId === AFFINITY_USER_TEAM_ID);
      const clubResult = clubLeagueResult ?? clubChampionsResult ?? clubKingsCupResult ?? clubAffinityResult;
      const clubTeamId = clubAffinityResult ? AFFINITY_USER_TEAM_ID : 'team_aurora';
      const clubIsHome = clubResult?.homeId === clubTeamId;
      const clubGoals = clubResult ? (clubIsHome ? clubResult.homeScore : clubResult.awayScore) : 0;
      const rivalGoals = clubResult ? (clubIsHome ? clubResult.awayScore : clubResult.homeScore) : 0;
      const decisiveResult = clubChampionsResult ?? clubKingsCupResult ?? (clubAffinityResult?.phase === 'FINAL' ? clubAffinityResult : undefined);
      const outcome: MatchRewardOutcome | null = decisiveResult
        ? decisiveResult.winnerId === clubTeamId ? 'win' : 'loss'
        : clubLeagueResult || clubAffinityResult ? clubGoals > rivalGoals ? 'win' : clubGoals === rivalGoals ? 'draw' : 'loss' : null;
      const decisiveWinner = decisiveResult?.winnerId;
      const correctedOutcome = decisiveWinner ? decisiveWinner === clubTeamId ? 'win' : 'loss' : outcome;
      const progressUpdate = correctedOutcome ? advanceClubProgress(current.clubProgress, {
        wins: correctedOutcome === 'win' ? 1 : 0,
        kickPlayerIds: calendarDevelopment.clubAwards.filter((award) => award.stat === 'kick').map((award) => award.playerId),
      }) : null;
      const group = findClubGroup(createLeagueGroups(current.leagueSeed, current.clubName));
      const schedule = createDoubleRoundRobinSchedule(group.teams.map((team) => team.id));
      const next: SaveGame = {
        ...current,
        careerDate: date,
        leagueResults: [...current.leagueResults, ...leagueResults],
        championsResults: [...current.championsResults, ...simulated],
        kingsCupResult: kingsCupResults[0] ?? current.kingsCupResult,
        affinityGamesResults: [...current.affinityGamesResults, ...affinityResults],
        leagueMatchday: clubLeagueResult ? Math.max(current.leagueMatchday, clubLeagueResult.matchday) : current.leagueMatchday,
        lastResult: clubResult ? { homeScore: clubResult.homeScore, awayScore: clubResult.awayScore, seed: current.nextSeed } : current.lastResult,
        tokens: correctedOutcome ? addTokenRewards(addTokenRewards(current.tokens, rollMatchTokenRewards(current.nextSeed, correctedOutcome)), progressUpdate!.rewards) : current.tokens,
        clubProgress: progressUpdate?.progress ?? current.clubProgress,
        clubDevelopment: calendarDevelopment.clubDevelopment,
        worldDevelopment: calendarDevelopment.worldDevelopment,
        developmentLog: calendarDevelopment.developmentLog,
        nextSeed: current.nextSeed + 911,
      };
      const withHonours = recordResolvedCompetitionHonours(next, simulated, kingsCupResults, affinityResults);
      const afterTitle = clubChampionsResult?.round === 'FINAL' && clubChampionsResult.winnerId === 'team_aurora'
        ? awardCompetitionTitle(withHonours, 'tournament')
        : withHonours;
      const afterKingsCup = clubKingsCupResult?.winnerId === 'team_aurora' ? awardCompetitionTitle(afterTitle, 'tournament') : afterTitle;
      const afterAffinity = clubAffinityResult?.phase === 'FINAL' && clubAffinityResult.winnerId === AFFINITY_USER_TEAM_ID
        ? awardAffinityGamesTitle(afterKingsCup)
        : afterKingsCup;
      return clubLeagueResult && afterAffinity.leagueMatchday >= schedule.length && canRolloverSeason(afterAffinity) ? rolloverSeason(afterAffinity) : afterAffinity;
    });
  }, []);

  const reset = useCallback(() => setSave(createFreshSave()), []);
  const dismissSeasonReview = useCallback(() => setSave((current) => current.seasonReview ? { ...current, seasonReview: null } : current), []);

  return { save, ownedPlayers, recoveredFromLocal: bootstrap.recoveredFromLocal, setFormation, setAffinityFormation, createCustomFormation, updateCustomFormationLayout, deleteCustomFormation, setTactic, moveLineupPlayer, moveAffinityLineupPlayer, swapReservePlayer, setCaptain, setTacticalRole, setAffinityTacticalRole, recruit, recruitBatch, consumeSpirits, redeemTechniqueChoice, confirmAffinitySquad, completeAffinityPresentation, registerResult, advanceCareerDay, dismissSeasonReview, reset };
}

type CalendarResolvedResult = CompetitionResult | ChampionsResult;

/**
 * Calendar simulation has no frame-by-frame match event stream, but it must
 * not freeze player careers. This produces a deterministic compact event log
 * from the resolved score, then feeds it through the exact same development
 * lottery and participant accounting used after a played match. Both sides
 * are processed symmetrically; the club-facing log only records the manager's
 * own awards so it remains useful rather than becoming a world-news feed.
 */
export function progressCalendarDevelopment(save: SaveGame, results: CalendarResolvedResult[]) {
  let clubDevelopment = save.clubDevelopment;
  let worldDevelopment = save.worldDevelopment;
  let developmentLog = save.developmentLog;
  const clubAwards: ReturnType<typeof rollMatchDevelopment>['awards'] = [];
  const groups = createLeagueGroups(save.leagueSeed, save.clubName);
  const rivals = new Map(groups.flatMap((group) => group.teams)
    .filter((team) => team.id !== 'team_aurora')
    .map((team) => [team.id, team]));

  results.forEach((result, resultIndex) => {
    const isChampions = result.competitionId === 'champions';
    const sides = [
      { teamId: result.homeId, goals: result.homeScore, conceded: result.awayScore, side: 0 as const },
      { teamId: result.awayId, goals: result.awayScore, conceded: result.homeScore, side: 1 as const },
    ];
    sides.forEach(({ teamId, goals, conceded, side }) => {
      // This mirrors the live Champions rule: the side advancing in a
      // knockout tie earns its restrained development roll, not both sides.
      if (isChampions && (!('winnerId' in result) || result.winnerId !== teamId)) return;
      const isClub = teamId === 'team_aurora';
      const rival = isClub ? undefined : rivals.get(teamId);
      if (!isClub && !rival) return;
      const ids = isClub ? save.lineup : rivalLineupIds(rival!);
      const records = isClub ? clubDevelopment : worldDevelopment;
      const playersInMatch = ids
        .map((id) => playerById.get(id))
        .filter(Boolean)
        .map((player) => resolveDevelopedPlayer(player!, records[player!.id]));
      if (!playersInMatch.length) return;

      const outcome: MatchDevelopmentOutcome = isChampions
        ? 'draw'
        : goals > conceded ? 'win' : goals === conceded ? 'draw' : 'loss';
      const seed = (save.nextSeed + resultIndex * 104729 + side * 313) >>> 0;
      const roll = rollMatchDevelopment(
        playersInMatch,
        simulatedDevelopmentEvents(playersInMatch, side, goals, seed),
        side,
        outcome,
        seed,
      );
      const apply = (target: Record<string, PlayerDevelopment>) => applyDevelopmentAwards(
        target,
        roll.awards,
        (id) => playerById.get(id),
        ids,
        outcome,
        roll.mvpId,
      );
      if (isClub) {
        clubDevelopment = apply(clubDevelopment);
        clubAwards.push(...roll.awards);
        developmentLog = [...createDevelopmentLog(roll.awards, save.season), ...developmentLog].slice(0, 120);
      } else {
        worldDevelopment = apply(worldDevelopment);
      }
    });
  });
  return { clubDevelopment, worldDevelopment, developmentLog, clubAwards };
}

/** The simulated score identifies the decisive actions while preserving an
 * unbiased, seeded selection of protagonists for non-scoring contribution. */
function simulatedDevelopmentEvents(playersInMatch: Player[], side: 0 | 1, goals: number, seed: number): MatchEvent[] {
  const rng = new SeededRng(seed ^ 0x3c6ef372);
  const pick = () => rng.pick(playersInMatch);
  const events: MatchEvent[] = [];
  for (let goal = 0; goal < Math.min(8, Math.max(0, goals)); goal += 1) {
    const scorer = pick();
    events.push({ second: goal * 60 + 30, type: 'GOAL', team: side, actorId: scorer.id, message: 'Gol simulado', homeScore: 0, awayScore: 0 });
  }
  const performer = pick();
  events.push({ second: 5400, type: goals > 0 ? 'SHOT' : 'DRIBBLE', team: side, actorId: performer.id, message: 'Actuación simulada', homeScore: 0, awayScore: 0 });
  return events;
}

/** Tournament growth is intentionally restrained: only the team that wins
 * improves, using the normal draw/loss table and without any MVP multiplier. */
function applyPlayedCareerDate(save: SaveGame, playedOn?: string, previousDate?: string): SaveGame {
  // A manually managed fixture may be launched from a future calendar cell.
  // Its real date, carried by the match screen, is the source of truth for
  // the career cursor. Never move the cursor backwards for an old replay.
  const date = [save.careerDate, previousDate, playedOn].filter((value): value is string => Boolean(value)).sort().at(-1)!;
  return date === save.careerDate ? save : { ...save, careerDate: date };
}

function registerChampionsResult(current: SaveGame, match: MatchResult): SaveGame {
  const fixture = nextClubFixture(current);
  if (!fixture || fixture.competition !== 'champions' || !fixture.round) return current;
  const clubIsHome = fixture.homeId === 'team_aurora';
  const clubGoals = clubIsHome ? match.homeScore : match.awayScore;
  const rivalGoals = clubIsHome ? match.awayScore : match.homeScore;
  const resolution = resolveChampionsTie(fixture.homeId, fixture.awayId, match.homeScore, match.awayScore, match.seed);
  const clubWins = resolution.winnerId === 'team_aurora';
  const result: ChampionsResult = { competitionId: 'champions', matchday: 0, fixtureId: fixture.id, round: fixture.round, homeId: fixture.homeId, awayId: fixture.awayId, homeScore: match.homeScore, awayScore: match.awayScore, ...resolution };
  let clubDevelopment = current.clubDevelopment;
  let worldDevelopment = current.worldDevelopment;
  let developmentLog = current.developmentLog;
  if (clubWins) {
    const clubPlayers = current.lineup.map((id) => resolveClubPlayer(current, id)).filter(Boolean) as Player[];
    const roll = rollMatchDevelopment(clubPlayers, match.events, clubIsHome ? 0 : 1, 'draw', match.seed);
    clubDevelopment = applyDevelopmentAwards(current.clubDevelopment, roll.awards, (id) => playerById.get(id), current.lineup, 'draw', undefined);
    developmentLog = [...createDevelopmentLog(roll.awards, current.season), ...developmentLog].slice(0, 120);
  } else {
    const opponentIds = (clubIsHome ? match.away.lineup : match.home.lineup);
    const opponentPlayers = opponentIds.map((id) => resolveWorldPlayer(current, id)).filter(Boolean) as Player[];
    const roll = rollMatchDevelopment(opponentPlayers, match.events, clubIsHome ? 1 : 0, 'draw', match.seed);
    worldDevelopment = applyDevelopmentAwards(current.worldDevelopment, roll.awards, (id) => playerById.get(id), opponentIds, 'draw', undefined);
  }
  // The user only plays one tie, but a complete knockout round needs every
  // other result before it can create the next set of opponents. Resolve the
  // remaining ties on that date immediately, never re-simulating the club.
  const withClubResult: SaveGame = { ...current, championsResults: [...current.championsResults, result] };
  const backgroundResults = simulateChampionsOnDate(withClubResult, fixture.date);
  let next: SaveGame = { ...withClubResult, lastResult: { homeScore: match.homeScore, awayScore: match.awayScore, seed: match.seed }, championsResults: [...withClubResult.championsResults, ...backgroundResults], clubDevelopment, worldDevelopment, developmentLog, tokens: addTokenRewards(current.tokens, rollMatchTokenRewards(match.seed, clubWins ? 'win' : 'loss')), nextSeed: match.seed + 104729, careerDate: fixture.date };
  next = recordResolvedCompetitionHonours(next, [result, ...backgroundResults], [], []);
  if (fixture.round === 'FINAL' && clubWins) next = awardCompetitionTitle(next, 'tournament');
  return canRolloverSeason(next) ? rolloverSeason(next) : next;
}

function registerKingsCupResult(current: SaveGame, match: MatchResult): SaveGame {
  const fixture = nextClubFixture(current);
  if (!fixture || fixture.competition !== 'kings_cup' || current.kingsCupResult) return current;
  const resolution = neutralWinner(fixture.homeId, fixture.awayId, match.homeScore, match.awayScore, match.seed);
  const result: KingsCupResult = {
    competitionId: KINGS_CUP_COMPETITION_ID, matchday: 1, fixtureId: fixture.id,
    homeId: fixture.homeId, awayId: fixture.awayId, homeScore: match.homeScore, awayScore: match.awayScore, ...resolution,
  };
  const clubWins = result.winnerId === 'team_aurora';
  let next: SaveGame = {
    ...current,
    kingsCupResult: result,
    lastResult: { homeScore: match.homeScore, awayScore: match.awayScore, seed: match.seed },
    careerDate: fixture.date,
    nextSeed: match.seed + 104729,
    tokens: addTokenRewards(current.tokens, rollMatchTokenRewards(match.seed, clubWins ? 'win' : 'loss')),
  };
  next = recordResolvedCompetitionHonours(next, [], [result], []);
  if (clubWins) next = awardCompetitionTitle(next, 'tournament');
  return canRolloverSeason(next) ? rolloverSeason(next) : next;
}

/** The temporary Affinity squad is resolved through its own save fields; it
 * never counts as a club lineup replacement. The final uses penalties so the
 * world title can never end without a winner. */
function registerAffinityGamesResult(current: SaveGame, match: MatchResult): SaveGame {
  const fixture = nextClubFixture(current);
  if (!fixture || fixture.competition !== 'affinity_games' || !fixture.affinityPhase || !current.affinityGames?.squadPlayerIds.length) return current;
  const clubIsHome = fixture.homeId === AFFINITY_USER_TEAM_ID;
  const resolution = fixture.affinityPhase === 'FINAL'
    ? neutralWinner(fixture.homeId, fixture.awayId, match.homeScore, match.awayScore, match.seed)
    : {};
  const result: AffinityGamesResult = {
    competitionId: AFFINITY_GAMES_COMPETITION_ID,
    matchday: fixture.affinityPhase === 'FINAL' ? 4 : Number(fixture.id.split('_')[2]) || 0,
    fixtureId: fixture.id, phase: fixture.affinityPhase,
    homeId: fixture.homeId, awayId: fixture.awayId,
    homeScore: match.homeScore, awayScore: match.awayScore, ...resolution,
  };
  const clubGoals = clubIsHome ? match.homeScore : match.awayScore;
  const rivalGoals = clubIsHome ? match.awayScore : match.homeScore;
  const outcome: MatchRewardOutcome = result.winnerId
    ? result.winnerId === AFFINITY_USER_TEAM_ID ? 'win' : 'loss'
    : clubGoals > rivalGoals ? 'win' : clubGoals === rivalGoals ? 'draw' : 'loss';
  const withClubResult: SaveGame = { ...current, affinityGamesResults: [...current.affinityGamesResults, result] };
  const backgroundResults = simulateAffinityGamesOnDate(withClubResult, fixture.date);
  let next: SaveGame = {
    ...withClubResult,
    affinityGamesResults: [...withClubResult.affinityGamesResults, ...backgroundResults],
    lastResult: { homeScore: match.homeScore, awayScore: match.awayScore, seed: match.seed },
    careerDate: fixture.date,
    nextSeed: match.seed + 104729,
    tokens: addTokenRewards(current.tokens, rollMatchTokenRewards(match.seed, outcome)),
  };
  next = recordResolvedCompetitionHonours(next, [], [], [result, ...backgroundResults]);
  if (result.phase === 'FINAL' && result.winnerId === AFFINITY_USER_TEAM_ID) next = awardAffinityGamesTitle(next);
  return canRolloverSeason(next) ? rolloverSeason(next) : next;
}

/** League standings can be complete while a Champions tie or a temporary
 * selection event remains on the calendar. A season only rolls once the club
 * has no unresolved competition of its own, preventing late knockout rounds
 * from being erased by an early league rollover. */
function canRolloverSeason(save: SaveGame) {
  const group = findClubGroup(createLeagueGroups(save.leagueSeed, save.clubName));
  const schedule = createDoubleRoundRobinSchedule(group.teams.map((team) => team.id));
  return save.leagueMatchday >= schedule.length && !nextClubFixture(save);
}

/** Starts a new league edition without touching the club, recruits, tactical
 * setup or any future player-development records. Only competition state is
 * cleared and redrawn. */
function rolloverSeason(completedSave: SaveGame): SaveGame {
  const completedGroups = createLeagueGroups(completedSave.leagueSeed, completedSave.clubName);
  const completedResults = completeLeagueSeason(completedGroups, completedSave.leagueResults, completedSave.leagueSeed);
  const nextSeason = completedSave.season + 1;
  const drawSeed = nextLeagueSeed(completedSave.leagueSeed, nextSeason, completedSave.nextSeed ^ 0x51A71);
  const leagueSeed = nextLeagueSeed(completedSave.leagueSeed, nextSeason, completedSave.nextSeed ^ 0x9E3779B);
  const clubGroup = findClubGroup(completedGroups);
  const wonLeague = createStandings(clubGroup, completedResults, completedSave.leaguePointAdjustments)[0]?.team.id === 'team_aurora';
  const titledSave = wonLeague ? awardCompetitionTitle(completedSave, 'league') : completedSave;
  const honouredSave = recordLeagueHonours(titledSave, completedGroups, completedResults);
  const championsDraw = nextSeason >= 2 ? createChampionsDraw(completedGroups, completedResults, nextSeason, drawSeed, completedSave.leaguePointAdjustments) : null;
  const kingsCupDraw = createKingsCupDraw(completedGroups, completedResults, nextSeason, drawSeed ^ 0x4b1a, completedSave.leaguePointAdjustments);
  const affinityGames = createAffinityGames(nextSeason, drawSeed ^ 0xa771);
  const seasonReview = createSeasonReview(honouredSave, clubGroup, completedResults, wonLeague);
  return {
    ...honouredSave,
    season: nextSeason,
    leagueSeed,
    leagueMatchday: 0,
    leagueResults: [],
    leaguePointAdjustments: {},
    championsDraw,
    championsResults: [],
    kingsCupDraw,
    kingsCupResult: null,
    affinityGames,
    affinityGamesResults: [],
    careerDate: `${2025 + nextSeason}-09-01`,
    seasonReview,
  };
}

/** Shared prize hook for every competition. League rollover calls it now;
 * the special-tournament resolver must call `awardTournamentTitle` exactly
 * once when its final becomes playable. */
function awardCompetitionTitle(save: SaveGame, competition: 'league' | 'tournament'): SaveGame {
  const progressUpdate = advanceClubProgress(save.clubProgress, competition === 'league' ? { leagueTitles: 1 } : { tournamentTitles: 1 });
  return {
    ...save,
    clubProgress: progressUpdate.progress,
    // Major titles use the same prestigious reward promised by the career
    // economy: a Master Choice, plus a Technique Choice for the winner.
    tokens: addTokenRewards(addTokenRewards(addTokenRewards(save.tokens, progressUpdate.rewards), { [TECHNIQUE_CHOICE_TOKEN_ID]: 1 }), { [MASTER_CHOICE_TOKEN_ID]: 1 }),
  };
}

function awardAffinityGamesTitle(save: SaveGame): SaveGame {
  const event = save.affinityGames;
  if (!event) return save;
  const progressUpdate = advanceClubProgress(save.clubProgress, { tournamentTitles: 1 });
  return {
    ...save,
    clubProgress: progressUpdate.progress,
    tokens: addTokenRewards(addTokenRewards(save.tokens, progressUpdate.rewards), { [affinityTokenId(event.element)]: 20 }),
  };
}

/** Adds an honour once. A calendar day can resolve a background fixture and
 * later be revisited by UI state, so the ledger must be idempotent. */
function recordCompetitionHonour(save: SaveGame, honour: CompetitionHonour): SaveGame {
  const alreadyRecorded = save.competitionHonours.some((entry) => (
    entry.teamId === honour.teamId
    && entry.competition === honour.competition
    && entry.season === honour.season
    && entry.groupName === honour.groupName
  ));
  return alreadyRecorded ? save : { ...save, competitionHonours: [...save.competitionHonours, honour] };
}

function recordLeagueHonours(save: SaveGame, groups: LeagueGroup[], results: CompetitionResult[]): SaveGame {
  return groups.reduce((next, group) => {
    const winner = createStandings(group, results, save.leaguePointAdjustments)[0]?.team;
    return winner ? recordCompetitionHonour(next, {
      teamId: winner.id,
      competition: 'league',
      season: save.season,
      groupName: group.name,
    }) : next;
  }, save);
}

/** Records winners for every final, not only the manager's own club. This is
 * what makes the rival report a real career history instead of static copy. */
function recordResolvedCompetitionHonours(
  save: SaveGame,
  championsResults: ChampionsResult[],
  kingsCupResults: KingsCupResult[],
  affinityResults: AffinityGamesResult[],
): SaveGame {
  let next = save;
  championsResults.filter((result) => result.round === 'FINAL' && result.winnerId).forEach((result) => {
    next = recordCompetitionHonour(next, { teamId: result.winnerId, competition: 'champions', season: next.season });
  });
  kingsCupResults.filter((result) => result.winnerId).forEach((result) => {
    next = recordCompetitionHonour(next, { teamId: result.winnerId, competition: 'kings_cup', season: next.season });
  });
  affinityResults.filter((result) => result.phase === 'FINAL' && result.winnerId).forEach((result) => {
    next = recordCompetitionHonour(next, { teamId: result.winnerId!, competition: 'affinity_games', season: next.season });
  });
  return next;
}

function createSeasonReview(save: SaveGame, group: LeagueGroup, results: import('./types').CompetitionResult[], leagueTitle: boolean): SeasonReview {
  const standings = createStandings(group, results, save.leaguePointAdjustments);
  const row = standings.find((entry) => entry.team.id === 'team_aurora') ?? standings[0];
  const clubResults = results.filter((result) => result.competitionId === group.id && (result.homeId === 'team_aurora' || result.awayId === 'team_aurora'));
  const leadingPlayers = save.ownedPlayerIds
    .map((playerId) => ({ playerId, player: resolveClubPlayer(save, playerId) }))
    .filter((entry): entry is { playerId: string; player: Player } => Boolean(entry.player))
    .sort((a, b) => playerRating(b.player) - playerRating(a.player) || a.player.name.localeCompare(b.player.name))
    .slice(0, 3)
    .map(({ playerId, player }) => ({ playerId, rating: playerRating(player) }));
  return {
    completedSeason: save.season,
    leaguePosition: row?.position ?? 0,
    points: row?.points ?? 0,
    played: clubResults.length,
    wins: row?.won ?? 0,
    draws: row?.drawn ?? 0,
    losses: row?.lost ?? 0,
    goalsFor: row?.goalsFor ?? 0,
    goalsAgainst: row?.goalsAgainst ?? 0,
    leagueTitle,
    championsQualified: (row?.position ?? Infinity) <= 2,
    developmentCount: save.developmentLog.filter((entry) => entry.season === save.season).length,
    clubLevel: save.clubProgress.level,
    clubXp: save.clubProgress.xp,
    achievementIds: save.clubProgress.completedAchievementIds.filter((id) => clubAchievements.some((achievement) => achievement.id === id)).slice(-4),
    recentRecruitIds: save.recruits.slice(0, 4).map((recruit) => recruit.playerId),
    leadingPlayers,
  };
}

export function awardTournamentTitle(save: SaveGame): SaveGame {
  return awardCompetitionTitle(save, 'tournament');
}

function isChampionsDraw(value: SaveGame['championsDraw'] | undefined): value is NonNullable<SaveGame['championsDraw']> {
  return Boolean(value
    && typeof value.season === 'number'
    && typeof value.sourceSeason === 'number'
    && typeof value.seed === 'number'
    && Array.isArray(value.pairings)
    && value.pairings.every((pairing) => typeof pairing?.id === 'string'
      && typeof pairing.firstTeamId === 'string'
      && typeof pairing.firstGroupId === 'string'
      && typeof pairing.secondTeamId === 'string'
      && typeof pairing.secondGroupId === 'string'));
}

function isKingsCupDraw(value: SaveGame['kingsCupDraw'] | undefined): value is NonNullable<SaveGame['kingsCupDraw']> {
  return Boolean(value && typeof value.season === 'number' && typeof value.sourceSeason === 'number'
    && typeof value.seed === 'number' && typeof value.date === 'string'
    && typeof value.firstTeamId === 'string' && typeof value.secondTeamId === 'string');
}

function isKingsCupResult(value: SaveGame['kingsCupResult'] | undefined): value is NonNullable<SaveGame['kingsCupResult']> {
  return Boolean(value && typeof value.fixtureId === 'string' && typeof value.winnerId === 'string'
    && typeof value.homeId === 'string' && typeof value.awayId === 'string'
    && Number.isFinite(value.homeScore) && Number.isFinite(value.awayScore));
}

function isAffinityGames(value: SaveGame['affinityGames'] | undefined): value is NonNullable<SaveGame['affinityGames']> {
  return Boolean(value && typeof value.season === 'number' && typeof value.seed === 'number'
    && ['FIRE', 'WIND', 'FOREST', 'MOUNTAIN'].includes(value.element)
    && typeof value.announcementDate === 'string' && typeof value.firstMatchDate === 'string'
    && Array.isArray(value.candidatePlayerIds) && Array.isArray(value.squadPlayerIds)
    && Array.isArray(value.lineupPlayerIds) && Array.isArray(value.benchPlayerIds));
}

/** Adds the tactical board introduced after the first Affinity editions while
 * preserving existing temporary squads from older saves. */
function normaliseAffinityGames(value: SaveGame['affinityGames'] | undefined, formationExists: (formationId: string) => boolean): AffinityGames | null {
  if (!isAffinityGames(value)) return null;
  const squadPlayerIds = [...new Set(value.squadPlayerIds.filter((id) => playerById.get(id)?.element === value.element))];
  const requestedLineup = [...new Set(value.lineupPlayerIds.filter((id) => squadPlayerIds.includes(id)))];
  const lineupPlayerIds = requestedLineup.length === 11
    ? requestedLineup
    : affinityLineup(squadPlayerIds) ?? squadPlayerIds.slice(0, 11);
  return {
    ...value,
    squadPlayerIds,
    formationId: typeof (value as Partial<AffinityGames>).formationId === 'string' && formationExists((value as Partial<AffinityGames>).formationId!)
      ? (value as Partial<AffinityGames>).formationId!
      : 'formation_4_4_2',
    lineupPlayerIds,
    benchPlayerIds: squadPlayerIds.filter((id) => !lineupPlayerIds.includes(id)),
    tacticalRoleOverrides: normaliseTacticalRoleOverrides(value.tacticalRoleOverrides, squadPlayerIds),
  };
}

function buildBench(ownedPlayerIds: string[], lineup: string[], requestedBench?: string[], formerLineup: string[] = []): string[] {
  const allowed = new Set(ownedPlayerIds);
  const starters = new Set(lineup);
  const candidates = requestedBench === undefined
    ? ownedPlayerIds.filter((id) => !starters.has(id))
    : [...requestedBench, ...formerLineup, ...ownedPlayerIds];
  return [...new Set(candidates)].filter((id) => allowed.has(id) && !starters.has(id)).slice(0, BENCH_LIMIT);
}

export type GameState = ReturnType<typeof useGameState>;

function normaliseDevelopmentMap(value: unknown): Record<string, PlayerDevelopment> {
  if (!value || typeof value !== 'object') return {};
  return Object.fromEntries(Object.entries(value as Record<string, unknown>)
    .filter(([playerId]) => playerById.has(playerId))
    .map(([playerId, record]) => [playerId, normaliseDevelopment(record)]));
}

function normaliseTacticalRoleOverrides(value: unknown, ownedPlayerIds: string[]): Record<string, TacticalRole> {
  if (!value || typeof value !== 'object') return {};
  const owned = new Set(ownedPlayerIds);
  return Object.fromEntries(Object.entries(value as Record<string, unknown>).flatMap(([playerId, role]) => {
    const player = playerById.get(playerId);
    return player && owned.has(playerId) && typeof role === 'string' && validateAssignedRole(player.position, role as TacticalRole)
      ? [[playerId, role as TacticalRole] as const]
      : [];
  }));
}

function normalisePointAdjustments(value: unknown): Record<string, number> {
  if (!value || typeof value !== 'object') return {};
  return Object.fromEntries(Object.entries(value as Record<string, unknown>)
    .flatMap(([teamId, points]) => typeof teamId === 'string' && Number.isFinite(Number(points))
      ? [[teamId, Math.max(-99, Math.min(99, Math.round(Number(points))))] as const]
      : []));
}

function normaliseCompetitionHonours(value: unknown): CompetitionHonour[] {
  if (!Array.isArray(value)) return [];
  const competitions = new Set<CompetitionHonour['competition']>(['league', 'champions', 'kings_cup', 'affinity_games']);
  const seen = new Set<string>();
  return value.flatMap((entry): CompetitionHonour[] => {
    if (!entry || typeof entry !== 'object') return [];
    const honour = entry as Partial<CompetitionHonour>;
    if (typeof honour.teamId !== 'string' || !competitions.has(honour.competition as CompetitionHonour['competition']) || typeof honour.season !== 'number' || !Number.isInteger(honour.season) || honour.season < 1) return [];
    const normalised: CompetitionHonour = {
      teamId: honour.teamId,
      competition: honour.competition as CompetitionHonour['competition'],
      season: honour.season,
      ...(typeof honour.groupName === 'string' && honour.groupName.trim() ? { groupName: honour.groupName.trim() } : {}),
    };
    const key = `${normalised.teamId}:${normalised.competition}:${normalised.season}:${normalised.groupName ?? ''}`;
    if (seen.has(key)) return [];
    seen.add(key);
    return [normalised];
  });
}

function normaliseSeasonReview(value: unknown): SeasonReview | null {
  if (!value || typeof value !== 'object') return null;
  const review = value as Partial<SeasonReview>;
  const numeric = (key: keyof Pick<SeasonReview, 'completedSeason' | 'leaguePosition' | 'points' | 'played' | 'wins' | 'draws' | 'losses' | 'goalsFor' | 'goalsAgainst' | 'developmentCount' | 'clubLevel' | 'clubXp'>) => Math.max(0, Math.floor(Number(review[key]) || 0));
  if (!numeric('completedSeason')) return null;
  return {
    completedSeason: numeric('completedSeason'), leaguePosition: numeric('leaguePosition'), points: numeric('points'), played: numeric('played'), wins: numeric('wins'), draws: numeric('draws'), losses: numeric('losses'), goalsFor: numeric('goalsFor'), goalsAgainst: numeric('goalsAgainst'), developmentCount: numeric('developmentCount'), clubLevel: numeric('clubLevel'), clubXp: numeric('clubXp'),
    leagueTitle: review.leagueTitle === true, championsQualified: review.championsQualified === true,
    achievementIds: Array.isArray(review.achievementIds) ? review.achievementIds.filter((id): id is string => typeof id === 'string' && clubAchievements.some((achievement) => achievement.id === id)).slice(-4) : [],
    recentRecruitIds: Array.isArray(review.recentRecruitIds) ? review.recentRecruitIds.filter((id): id is string => typeof id === 'string' && playerById.has(id)).slice(0, 4) : [],
    leadingPlayers: Array.isArray(review.leadingPlayers) ? review.leadingPlayers.flatMap((entry) => entry && typeof entry === 'object' && typeof (entry as { playerId?: unknown }).playerId === 'string' && playerById.has((entry as { playerId: string }).playerId)
      ? [{ playerId: (entry as { playerId: string }).playerId, rating: Math.max(0, Math.floor(Number((entry as { rating?: unknown }).rating) || 0)) }]
      : []).slice(0, 3) : [],
  };
}

function normaliseRecruits(value: unknown): SaveGame['recruits'] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const recruit = item as Partial<SaveGame['recruits'][number]>;
    if (typeof recruit.playerId !== 'string' || !playerById.has(recruit.playerId)) return [];
    return [{
      playerId: recruit.playerId,
      techniqueIds: normaliseTechniqueIds(recruit.techniqueIds),
      recruitedAt: typeof recruit.recruitedAt === 'string' ? recruit.recruitedAt : new Date(0).toISOString(),
    }];
  });
}

function normaliseClubTechniqueIds(value: unknown, recruits: SaveGame['recruits']): Record<string, string[]> {
  const assignments: Record<string, string[]> = {};
  if (value && typeof value === 'object') {
    for (const [playerId, techniqueIds] of Object.entries(value as Record<string, unknown>)) {
      if (playerById.has(playerId)) assignments[playerId] = normaliseTechniqueIds(techniqueIds);
    }
  }
  // Older saves already recorded the gacha result in `recruits`; retain it
  // when adding the persistent loadout map instead of rerolling the player.
  for (const recruit of recruits) {
    if (!(recruit.playerId in assignments)) assignments[recruit.playerId] = [...recruit.techniqueIds];
  }
  return assignments;
}

function normaliseTechniqueIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((id): id is string => typeof id === 'string' && techniqueById.has(id)))];
}

function normaliseDevelopmentLog(value: unknown): SaveGame['developmentLog'] {
  if (!Array.isArray(value)) return [];
  const sources = new Set(['match', 'mvp', 'tournament', 'spirit']);
  const stats = new Set<PlayerStatKey>(['kick', 'dribble', 'pass', 'defense', 'speed', 'power', 'control', 'stamina']);
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const entry = item as Partial<SaveGame['developmentLog'][number]>;
    const rawStat = (item as { stat?: unknown }).stat;
    const stat = rawStat === 'physical' ? 'power' : rawStat;
    if (typeof entry.id !== 'string' || typeof entry.playerId !== 'string' || !playerById.has(entry.playerId)
      || typeof stat !== 'string' || !stats.has(stat as PlayerStatKey)
      || typeof entry.source !== 'string' || !sources.has(entry.source) || !Number.isFinite(entry.season)) return [];
    return [{
      id: entry.id,
      playerId: entry.playerId,
      stat: stat as PlayerStatKey,
      source: entry.source as SaveGame['developmentLog'][number]['source'],
      season: Math.max(1, Math.floor(Number(entry.season))),
      matchSeed: Number.isFinite(entry.matchSeed) ? Number(entry.matchSeed) : undefined,
      createdAt: typeof entry.createdAt === 'string' ? entry.createdAt : new Date(0).toISOString(),
    }];
  }).slice(0, 120);
}

function copyWorldDevelopment(source?: PlayerDevelopment): PlayerDevelopment {
  const world = normaliseDevelopment(source);
  return { ...emptyDevelopment(), boosts: { ...world.boosts } };
}

function progressSimulatedRivals(initial: Record<string, PlayerDevelopment>, group: ReturnType<typeof findClubGroup>, results: ReturnType<typeof completeLeagueMatchday>, seed: number) {
  let worldDevelopment = initial;
  const teamsById = new Map(group.teams.map((team) => [team.id, team]));
  results.forEach((result, index) => {
    if (result.homeId === 'team_aurora' || result.awayId === 'team_aurora') return;
    ([
      [result.homeId, result.homeScore, result.awayScore, 0],
      [result.awayId, result.awayScore, result.homeScore, 1],
    ] as const).forEach(([teamId, goals, conceded, side]) => {
      const team = teamsById.get(teamId);
      if (!team) return;
      const ids = rivalLineupIds(team);
      const players = ids.map((id) => playerById.get(id)).filter(Boolean).map((player) => resolveDevelopedPlayer(player!, worldDevelopment[player!.id]));
      const outcome: MatchDevelopmentOutcome = goals > conceded ? 'win' : goals === conceded ? 'draw' : 'loss';
      // Background matches do not have a full event stream. Give a different,
      // seeded participant a tiny decisive action so that the same alphabetical
      // player is not declared MVP in every simulated fixture.
      const simulationSeed = seed + index * 104729 + side * 313;
      const performer = players.length ? new SeededRng(simulationSeed ^ 0x3c6ef372).pick(players) : undefined;
      const events = performer ? [{
        second: 0,
        type: 'DRIBBLE' as const,
        team: side,
        actorId: performer.id,
        message: 'Actuación simulada',
        homeScore: 0,
        awayScore: 0,
      }] : [];
      const roll = rollMatchDevelopment(players, events, side, outcome, simulationSeed);
      worldDevelopment = applyDevelopmentAwards(worldDevelopment, roll.awards, (id) => playerById.get(id), ids, outcome, roll.mvpId);
    });
  });
  return worldDevelopment;
}

function groupWithCurrentDevelopment(group: ReturnType<typeof findClubGroup>, save: SaveGame) {
  return {
    ...group,
    teams: group.teams.map((team) => {
      const roster = team.id === 'team_aurora'
        ? save.lineup.map((id) => resolveClubPlayer(save, id)).filter(Boolean) as Player[]
        : team.players.map((player) => resolveDevelopedPlayer(player, save.worldDevelopment[player.id]));
      if (!roster.length) return team;
      const strongest = [...roster].sort((left, right) => playerRating(right) - playerRating(left)).slice(0, 11);
      return {
        ...team,
        players: roster,
        rating: Math.round(strongest.reduce((total, player) => total + playerRating(player), 0) / strongest.length),
      };
    }),
  };
}

function rivalLineupIds(team: RivalTeam) {
  return selectRivalLineup(team)?.map((player) => player.id) ?? [];
}

export function arrangeLineup(formationId: string, candidates: string[], customFormations: Formation[] = []): string[] | null {
  const formation = formationById.get(formationId) ?? customFormations.find((item) => item.id === formationId);
  if (!formation) return null;
  return arrangeLineupForFormation(formation, candidates);
}

function arrangeLineupForFormation(formation: Formation, candidates: string[]): string[] | null {
  const used = new Set<string>();
  const lineup = formation.roles.map((role) => {
    const preferred = preferredPositions(role);
    const playerId = candidates.find((id) => !used.has(id) && preferred.includes(playerById.get(id)?.position ?? 'FW'))
      ?? candidates.find((id) => !used.has(id));
    if (playerId) used.add(playerId);
    return playerId;
  });
  return lineup.some((id) => !id) ? null : lineup as string[];
}

/**
 * Rolls the exact post-match reward table. The match seed is the sole random
 * input, so a replay or a later UI render cannot alter the earned tokens.
 */
export function rollMatchTokenRewards(seed: number, outcome: MatchRewardOutcome): Record<string, number> {
  const rng = new SeededRng(seed ^ 0x5f3759df);
  const elementalProbability = outcome === 'win' ? .2 : .15;
  const rewards: Record<string, number> = {};
  const rolls = rng.int(2, 5);

  for (let index = 0; index < rolls; index += 1) {
    const roll = rng.next();
    const elementalIndex = Math.floor(roll / elementalProbability);
    const tokenId = elementalIndex < ELEMENT_TOKEN_IDS.length
      ? ELEMENT_TOKEN_IDS[elementalIndex]
      : 'token_youth';
    rewards[tokenId] = (rewards[tokenId] ?? 0) + 1;
  }

  // A win can add exactly one master-choice ticket. It is an extra reward,
  // never one of the 2–5 standard token rolls.
  if (outcome === 'win' && rng.next() < .02) {
    rewards[MASTER_CHOICE_TOKEN_ID] = 1;
  }
  // A Technique Choice ticket upgrades an owned player's loadout instead of
  // recruiting a copy. It is intentionally a little more frequent than the
  // master pick, but remains an extra reward on top of the 2–5 roll table.
  if (outcome === 'win' && rng.next() < .07) {
    rewards[TECHNIQUE_CHOICE_TOKEN_ID] = (rewards[TECHNIQUE_CHOICE_TOKEN_ID] ?? 0) + 1;
  }
  return rewards;
}

function addTokenRewards(tokens: Record<string, number>, rewards: Record<string, number>) {
  const next = { ...tokens };
  for (const [tokenId, amount] of Object.entries(rewards)) {
    next[tokenId] = (next[tokenId] ?? 0) + amount;
  }
  return next;
}

export function masterChoiceCandidates(save: SaveGame): Player[] {
  return players.filter((player) => !save.ownedPlayerIds.includes(player.id) && hasAssignedTeam(player));
}

type RecruitmentPlan = { results: RecruitResult[]; nextRecruitmentSeed: number };

/** Plans a complete pull before mutating React state. Each selected player is
 * immediately removed from the virtual pool, so a multi-pull has no internal
 * duplicates even though every pull is independently random. */
export function planRecruitmentBatch(save: SaveGame, tokenId: string, requestedAmount: number, selectedPlayerId?: string): RecruitmentPlan {
  const available = Math.max(0, Math.floor(save.tokens[tokenId] ?? 0));
  const amount = tokenId === MASTER_CHOICE_TOKEN_ID ? Math.min(1, available) : Math.min(available, Math.max(1, Math.floor(requestedAmount)));
  const results: RecruitResult[] = [];
  let virtualSave = save;

  for (let index = 0; index < amount; index += 1) {
    const result = selectRecruit(virtualSave, tokenId, selectedPlayerId);
    if (!result) break;
    results.push(result);
    virtualSave = {
      ...virtualSave,
      ownedPlayerIds: [...virtualSave.ownedPlayerIds, result.player.id],
      tokens: { ...virtualSave.tokens, [tokenId]: Math.max(0, (virtualSave.tokens[tokenId] ?? 0) - 1) },
      recruitmentSeed: nextRecruitmentSeed(virtualSave.recruitmentSeed),
    };
  }
  return { results, nextRecruitmentSeed: virtualSave.recruitmentSeed };
}

function applyRecruitmentPlan(current: SaveGame, source: SaveGame, tokenId: string, plan: RecruitmentPlan): SaveGame {
  // The reveal blocks concurrent actions. This guard nevertheless protects a
  // batch from consuming tickets after a stale render or a slot replacement.
  if (!plan.results.length || current.recruitmentSeed !== source.recruitmentSeed
    || current.nextSeed !== source.nextSeed || (current.tokens[tokenId] ?? 0) < plan.results.length
    || plan.results.some((result) => current.ownedPlayerIds.includes(result.player.id))) return current;

  const recruitedIds = plan.results.map((result) => result.player.id);
  const timestamp = Date.now();
  const clubDevelopment = { ...current.clubDevelopment };
  const clubTechniqueIds = { ...current.clubTechniqueIds };
  plan.results.forEach(({ player }) => {
    // A recruitment is an independent copy of the current world version.
    clubDevelopment[player.id] = copyWorldDevelopment(current.worldDevelopment[player.id]);
  });
  const recruitedElements = plan.results.reduce<Record<string, number>>((totals, result) => {
    const key = `${result.player.element.toLowerCase()}Recruits`;
    totals[key] = (totals[key] ?? 0) + 1;
    return totals;
  }, {});
  const progressUpdate = advanceClubProgress(current.clubProgress, {
    recruits: plan.results.length,
    fireRecruits: recruitedElements.fireRecruits ?? 0,
    windRecruits: recruitedElements.windRecruits ?? 0,
    forestRecruits: recruitedElements.forestRecruits ?? 0,
    mountainRecruits: recruitedElements.mountainRecruits ?? 0,
  });
  return {
    ...current,
    ownedPlayerIds: [...current.ownedPlayerIds, ...recruitedIds],
    clubDevelopment,
    clubTechniqueIds: {
      ...clubTechniqueIds,
      ...Object.fromEntries(plan.results.map((result) => [result.player.id, [...result.techniques]])),
    },
    tokens: addTokenRewards({ ...current.tokens, [tokenId]: current.tokens[tokenId] - plan.results.length }, progressUpdate.rewards),
    recruits: [
      ...plan.results.map((result, index) => ({
        playerId: result.player.id,
        techniqueIds: result.techniques,
        // Consecutive millisecond timestamps keep history keys stable while
        // remaining valid ISO dates for exports and future sorting.
        recruitedAt: new Date(timestamp + index).toISOString(),
      })),
      ...current.recruits,
    ].slice(0, 20),
    spiritReleasedPlayerIds: current.spiritReleasedPlayerIds.filter((id) => !recruitedIds.includes(id)),
    recruitmentSeed: plan.nextRecruitmentSeed,
    nextSeed: (current.nextSeed + 104729 * plan.results.length) >>> 0 || 1,
    clubProgress: progressUpdate.progress,
  };
}

function selectRecruit(save: SaveGame, tokenId: string, selectedPlayerId?: string): RecruitResult | null {
  if ((save.tokens[tokenId] ?? 0) <= 0) return null;
  const rng = new SeededRng(save.recruitmentSeed);
  const element = tokenId.replace('token_', '').toUpperCase() as ElementType;
  const candidates = players.filter((player) => {
    if (save.ownedPlayerIds.includes(player.id)) return false;
    const team = player.metadata?.team?.toLowerCase() ?? '';
    const isYouth = player.recruitment_source === 'youth' || team === 'unaffiliated' || team === 'sin equipo';
    if (tokenId === MASTER_CHOICE_TOKEN_ID) return hasAssignedTeam(player);
    return tokenId === 'token_youth' ? isYouth : !isYouth && player.element === element;
  });
  if (!candidates.length) return null;
  const player = tokenId === MASTER_CHOICE_TOKEN_ID
    ? candidates.find((candidate) => candidate.id === selectedPlayerId)
    : pickRecruitCandidate(candidates, save.spiritReleasedPlayerIds, rng);
  if (!player) return null;
  const mutable = [...(player.available_techniques ?? player.techniques ?? [])].filter((id) => techniqueById.has(id));
  const techniques: string[] = [];
  while (techniques.length < 2 && mutable.length) {
    const choice = rng.pick(mutable);
    techniques.push(choice);
    mutable.splice(mutable.indexOf(choice), 1);
  }
  return { player, techniques };
}

function pickRecruitCandidate(candidates: Player[], spiritReleasedPlayerIds: string[], rng: SeededRng): Player {
  const released = new Set(spiritReleasedPlayerIds);
  const totalWeight = candidates.reduce((total, candidate) => total + (released.has(candidate.id) ? SPIRIT_RETURN_WEIGHT : 1), 0);
  let roll = rng.next() * totalWeight;
  for (const candidate of candidates) {
    roll -= released.has(candidate.id) ? SPIRIT_RETURN_WEIGHT : 1;
    if (roll <= 0) return candidate;
  }
  return candidates.at(-1)!;
}

function nextRecruitmentSeed(seed: number): number {
  // Small integer mixer. It yields a fresh deterministic cursor from the
  // persisted entropy seed without relying on a request or Math.random after
  // the pull has started.
  let value = (seed + 0x6d2b79f5) >>> 0;
  value = Math.imul(value ^ (value >>> 15), value | 1);
  value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
  return (value ^ (value >>> 14)) >>> 0 || 1;
}

function hasAssignedTeam(player: Player) {
  const team = player.metadata?.team?.trim().toLocaleLowerCase() ?? '';
  return Boolean(team) && !['unaffiliated', 'sin equipo', 'unknown', 'sub character'].includes(team);
}

function preferredPositions(role: string): Player['position'][] {
  if (role === 'GK') return ['GK'];
  if (['LB', 'CB', 'RB'].includes(role)) return ['DF'];
  if (['LWB', 'RWB'].includes(role)) return ['DF', 'MF'];
  if (['LM', 'RM'].includes(role)) return ['MF', 'DF'];
  if (['CM', 'DM', 'AM'].includes(role)) return ['MF'];
  if (['LW', 'RW'].includes(role)) return ['FW', 'MF'];
  return ['FW'];
}
